import React, { useEffect } from "react";
import AOS from "aos";
import "./CustomSoftware.css";
import SEO from "../../SEO";

const softwareServices = [
  {
    title: "Desktop Applications",
    description:
      "We build high-performance desktop applications using C# WinForms and WPF. Ideal for ERP systems, inventory management, and enterprise-level business automation.",
    features: [
      "C# WinForms",
      "WPF UI/UX",
      "ERP Systems",
      "Inventory Solutions",
    ],
    img: process.env.PUBLIC_URL + "/img/software-development.gif",
    icon: "bi-pc-display",
  },
  {
    title: "Web Applications",
    description:
      "Modern web applications using ASP.NET Core, React.js, and Vue.js with secure REST APIs, scalable architecture, and real-time data processing.",
    features: ["ASP.NET Core", "React.js", "Vue.js", "REST APIs"],
    img: process.env.PUBLIC_URL + "/img/Website-Design-Services.gif",
    icon: "bi-globe",
  },
  {
    title: "Cross-Platform Apps",
    description:
      "Build once, run anywhere applications using Electron.js for Windows, macOS, and Linux with unified codebase and high performance.",
    features: [
      "Electron.js",
      "Cross Platform",
      "Single Codebase",
      "Business Tools",
    ],
    img: process.env.PUBLIC_URL + "/img/CrossPlateForm.gif",
    icon: "bi-laptop",
  },
  {
    title: "Full-Stack Solutions",
    description:
      "End-to-end development using C#, ASP.NET, React.js, and cloud integration for scalable enterprise applications.",
    features: [
      "C# & ASP.NET",
      "React.js",
      "Database Integration",
      "Cloud Deployment",
    ],
    img: process.env.PUBLIC_URL + "/img/FullStack.gif",
    icon: "bi-code-slash",
  },
  {
    title: "Mobile Applications",
    description:
      "High-performance mobile apps for Android & iOS using .NET MAUI and React Native with smooth UI and backend integration.",
    features: [".NET MAUI", "React Native", "Android & iOS", "API Integration"],
    img: process.env.PUBLIC_URL + "/img/mobilegif.gif",
    icon: "bi-phone",
  },
];

export default function CustomSoftware() {
  useEffect(() => {
    AOS.init({ duration: 800, offset: 100 });
  }, []);

  return (
    <>
      <SEO
        title="Custom Software Development – ERP, Web, Desktop & Mobile Solutions"
        description="MZR Software provides custom ERP, inventory, POS, web, desktop and mobile software solutions for business automation."
        image="https://mzrsoftware.com/assets/images/og-customsoftware.jpg"
        url="https://mzrsoftware.com/OurServices/CustomSoftware/customsoftware"
      />

      <div className="custom-software-page">
        {/* HERO */}
        <section className="text-center section-header" data-aos="fade-up">
          <h1 className="section-title">
            Custom <span className="highlight">Software Development</span>
          </h1>

          <p className="section-subtitle">
            ERP, POS, Inventory, Desktop, Web & Mobile solutions for modern
            business automation and scalability.
          </p>
        </section>

        {/* INTRODUCTION */}
        <section className="container text-center mb-5" data-aos="fade-up">
          <h2>Introduction</h2>
          <p>
            We design and develop custom software solutions that help businesses
            automate operations, manage data, and scale efficiently using modern
            technologies.
          </p>
        </section>

        {/* FEATURES */}
        <section className="container mb-5">
          <h2 className="text-center mb-4">Features</h2>

          <div className="services-grid">
            {softwareServices.map((service, index) => (
              <div
                key={index}
                className={`service-card zig-zag ${index % 2 === 0 ? "left" : "right"}`}
                data-aos="fade-up"
              >
                <div className="card-content">
                  <span className={`bi ${service.icon} service-icon`}></span>
                  <h3>{service.title}</h3>
                  <p>{service.description}</p>

                  <div className="service-features">
                    {service.features.map((f, i) => (
                      <span key={i} className="feature-tag">
                        {f}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="card-image">
                  <img src={service.img} alt={service.title} />
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* BENEFITS */}
        <section className="container mb-5 text-center" data-aos="fade-up">
          <h2>Benefits</h2>
          <div className="services-grid">
            {[
              "Faster Business Automation",
              "Reduce Manual Work",
              "Scalable Architecture",
              "Cloud Integration",
              "Secure Systems",
              "Custom Workflow Design",
            ].map((item, i) => (
              <div key={i} className="service-card">
                <div className="card-content">
                  <span className="bi bi-check-circle service-icon"></span>
                  <h3>{item}</h3>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* SCREENSHOTS */}
        <section className="container mb-5 text-center" data-aos="fade-up">
          <h2>Screenshots</h2>
          <p>ERP, POS, Inventory, Web & Mobile UI previews will appear here.</p>
        </section>

        {/* INDUSTRIES */}
        <section className="container mb-5 text-center" data-aos="fade-up">
          <h2>Industries</h2>

          <div className="service-features justify-content-center">
            {[
              "Retail",
              "Pharmacy",
              "Supermarket",
              "Warehouse",
              "Education",
              "Healthcare",
              "E-Commerce",
              "Manufacturing",
            ].map((item, i) => (
              <span key={i} className="feature-tag">
                {item}
              </span>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section className="container mb-5" data-aos="fade-up">
          <h2 className="text-center">FAQs</h2>

          <div className="services-grid">
            <div className="service-card">
              <div className="card-content">
                <h3>Do you build ERP systems?</h3>
                <p>Yes, we build full ERP systems for businesses.</p>
              </div>
            </div>

            <div className="service-card">
              <div className="card-content">
                <h3>Do you offer POS software?</h3>
                <p>Yes, we provide complete POS & inventory systems.</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="text-center mb-5" data-aos="zoom-in">
          <h2>Let’s Build Your Custom Software</h2>
          <p>ERP, POS, Inventory & Business Automation Solutions</p>

          <a href="/contact" className="btn-primary">
            Get Free Consultation →
          </a>
        </section>
      </div>
    </>
  );
}
